<script setup lang="ts">
import { computed, onBeforeMount, ref, toRefs, watch, inject } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { formatNumber } from "chartjs-graph";
import { useApiClient } from "@/api/client";
import GraphTotalDTO = App.Data.Explorer.GraphTotalDTO;
import { processedDocument } from "chartjs-graph";
import {
	derivedDocumentKey,
	documentKey,
} from "@/modules/explorer/ExplorerUtils";
import CompareTooltip, {
	CompareTooltipMetric,
} from "@/modules/explorer/components/Metrics/Table/CompareTooltip.vue";
import { orderComparisonData } from "chartjs-graph";

const derivedDocument = inject(derivedDocumentKey);
const document = inject(documentKey);

const props = defineProps<{
	lastUpdated: Date;
	isGraphLoading?: boolean;
	needsDataRefresh?: () => boolean;
}>();

const { lastUpdated } = toRefs(props);
const graphTotals = ref<GraphTotalDTO[]>([]);
const isLoading = ref(true);

const comparisonTotals = computed((): CompareTooltipMetric => {
	return {
		values: graphTotals.value.map((gt) => {
			return {
				...gt,
				comparison: gt?.label,
			};
		}),
		format: totalsFormatting.value,
	};
});

const getTotalsFromBackEnd = async (isBackgroundUpdate: boolean = false) => {
	if (!isBackgroundUpdate) {
		isLoading.value = true;
	}

	const response = await useApiClient("explorer").fetchTotalsForDocument(
		processedDocument(document.value)
	);
	if (response.isOk()) {
		graphTotals.value = orderComparisonData(
			derivedDocument.value,
			response.value,
			(v) => v.label
		);
	}
	isLoading.value = false;
};

onBeforeMount(() => {
	getTotalsFromBackEnd();
});

watch(lastUpdated, async () => {
	if (props.needsDataRefresh && !props.needsDataRefresh()) {
		return;
	}
	getTotalsFromBackEnd();
});

const isUpdatingInBackground = computed(
	() => useExplorerStore().explorerState.isDataLoadingInBackground
);

const isTopMetricSupported = computed(
	() =>
		!derivedDocument.value.is_horizontal_top_x_enabled ||
		(derivedDocument.value.is_horizontal_top_x_enabled &&
			derivedDocument.value
				.is_aggregate_horizontal_remaining_categories_enabled)
);

const totalsFormatting = computed(() => {
	const verticals_potentially_computed_to_percentages = [
		"occupancyavg",
		"occupancyavgmax",
		"occupancyavgmin",
		"occupancymax",
		"occupancymin",
		"car_rental_occupancyavg",
		"car_rental_occupancymax",
		"car_rental_occupancymin",
		"charging_occupancyavg",
		"charging_occupancymax",
		"charging_occupancymin",
		"charging_occupancyavgmax",
		"charging_occupancyavgmin",
		"charging_chargers_occupancyavg",
		"charging_chargers_occupancymax",
		"charging_chargers_occupancymin",
		"lodging_occupancyavg",
		"lodging_occupancymax",
		"lodging_occupancymin",
		"screentimehour",
		"audiencedistr",
	];
	if (
		derivedDocument.value.is_visualised_as_percentage_share &&
		verticals_potentially_computed_to_percentages.includes(
			derivedDocument.value.vertical.name_clean
		)
	) {
		return "percentage";
	}
	return derivedDocument.value.vertical.format;
});

watch(isUpdatingInBackground, (newValue) => {
	if (newValue) {
		getTotalsFromBackEnd(true);
	}
});
</script>

<template>
	<template
		v-if="
			graphTotals?.length > 0 &&
			derivedDocument &&
			!isLoading &&
			!isGraphLoading
		"
	>
		<div style="height: 75px; position: relative">
			<div>
				<div class="metric-type-text my-n1">
					{{ $t(`properties.${derivedDocument.vertical.name_clean}.title`) }}
				</div>
				<div class="total_amount_text" v-if="isTopMetricSupported">
					{{ formatNumber(graphTotals[0].totalValue, totalsFormatting) }}
				</div>
				<div v-else style="padding-top: 1px">Not available</div>
			</div>
			<div
				style="margin-top: -8px"
				class="d-flex gap-2 align-center"
				v-if="
					graphTotals?.length > 1 &&
					derivedDocument.vertical.format !== 'rate_card'
				"
			>
				<span class="text-subtitle-1 font-weight-bold text-no-wrap">
					{{ graphTotals[1].deltaRelative > 0 ? "+" : ""
					}}{{ formatNumber(graphTotals[1].deltaAbsolute, totalsFormatting) }}
				</span>

				<compare-tooltip :metric="comparisonTotals">
					<v-chip
						class="ml-2"
						density="compact"
						label
						:prepend-icon="
							graphTotals[1].deltaRelative === 0
								? 'mdi-minus'
								: graphTotals[1].deltaRelative > 0
								? 'mdi-chevron-up'
								: 'mdi-chevron-down'
						"
						:color="
							graphTotals[1].deltaRelative === 0
								? 'warning'
								: graphTotals[1].deltaRelative > 0
								? 'success'
								: 'error'
						"
					>
						<div>
							<span class="text-caption">
								{{
									formatNumber(
										Math.abs(graphTotals[1].deltaRelative),
										"percentage"
									)
								}}
							</span>
						</div>
					</v-chip>
				</compare-tooltip>
			</div>
		</div>
	</template>
	<div
		class="d-flex align-center justify-center"
		style="width: 135px; height: 75px"
		v-else
	>
		<v-progress-linear
			v-if="isLoading && isGraphLoading"
			color="primary"
			indeterminate
		/>
		<div class="no-data-text" v-else-if="graphTotals?.length === 0">
			No Data
		</div>
	</div>
	<v-divider class="mx-5" vertical />
</template>

<style lang="scss" scoped>
.metric-type-text {
	font-size: 0.8rem;
	font-weight: 500;
}
.total_amount_text {
	font-size: 1.7rem;
	font-weight: 500;
}

.no-data-text {
	font-weight: 700;
}

.v-skeleton-loader__text {
	padding: 0 0 !important;
	margin: 0 0 !important;
}
.v-expansion-panel-title {
	background: rgba(var(--v-theme-primary), 0.1) !important;
}
.tooltip-table th,
.tooltip-table td {
	padding: 0 5px;
	font-size: 0.75rem !important;
	font-weight: 400;
	line-height: 1.667;
	letter-spacing: 0.0333333333em !important;
	font-family: "Roboto", sans-serif;
	text-transform: none !important;
}
</style>
